import * as THREE from "three";
import type { World as SceneWorld } from "./scene";
import type { PhysicsBody, PhysicsHandle } from "./physics";

// Higher = snappier approach. With dt at 60Hz, 5 reaches ~99% in ~0.9s.
const APPROACH_RATE = 5;
const ARRIVED_EPSILON = 0.01;
// Distance from orbit target to camera while focused. Tuned so the cup
// fills a comfortable portion of the viewport at default FOV (45°).
const FOCUS_CAMERA_DISTANCE = 3.5;

interface FocusedCup {
  body: PhysicsBody;
  group: THREE.Group;
  /** Body position at the moment focus was entered — where we return to. */
  origPos: { x: number; y: number; z: number };
  /** Body rotation at the moment focus was entered — restored on exit. */
  origRot: { x: number; y: number; z: number; w: number };
  /** Camera position at focus entry — restored on exit. */
  origCameraPos: THREE.Vector3;
  /** Resting rotation from physics — what we lerp toward during focus so a
   * tipped-over cup is righted before being shown to the user. */
  focusRot: { x: number; y: number; z: number; w: number };
  /** Once true, the per-frame loop lerps the cup back home. */
  exiting: boolean;
}

export interface FocusHandle {
  enter: (group: THREE.Group) => void;
  exit: () => void;
  /** True from the moment a cup is focused until the exit lerp finishes
   * and snapRestore runs — covers entering, held, and exiting states. */
  isActive: () => boolean;
}

/**
 * Focus mode: when a cup is selected, lift it to the center of the camera
 * view and hide everything else (shelf and other cups) so only the cup and
 * the panorama backdrop remain. On exit, lerp the cup back home and reveal
 * the rest of the scene.
 *
 * The focused cup's rigid body is fully disabled (excluded from the
 * Rapier simulation) and its position is driven directly via
 * setTranslation each frame — physics.ts's per-frame body→mesh sync still
 * reads the translation, so the visible mesh follows the lerp. Disabling
 * the body avoids the spurious contact impulses Rapier emits when a body
 * type flips from Dynamic to Kinematic mid-simulation, which used to
 * launch nearby cups dozens of meters across the scene.
 */
export function attachFocus(
  scene: SceneWorld,
  physics: PhysicsHandle,
): FocusHandle {
  let focused: FocusedCup | null = null;
  const targetPos = new THREE.Vector3();
  const camDir = new THREE.Vector3();
  const targetCamPos = new THREE.Vector3();
  const tmpQuatCur = new THREE.Quaternion();
  const tmpQuatTarget = new THREE.Quaternion();
  // Children of cupRoot that hideOthers turned off — restored verbatim by
  // showAll. Tracking the exact set (instead of blanket-showing everything)
  // preserves the visibility of currently-shattered cup groups, whose mesh
  // is intentionally invisible while their fragments are scattered.
  const hiddenChildren: THREE.Object3D[] = [];

  function hideOthers(focusedGroup: THREE.Group): void {
    scene.shelf.visible = false;
    scene.floor.visible = false;
    hiddenChildren.length = 0;
    // Iterate cupRoot's children directly so cup-fragment meshes (added
    // there by physics.performFracture) are hidden too, not just the
    // intact cup groups in cupMap.
    for (const child of scene.cupRoot.children) {
      if (child === focusedGroup) continue;
      if (child.visible) {
        child.visible = false;
        hiddenChildren.push(child);
      }
    }
  }

  function showAll(): void {
    scene.shelf.visible = true;
    scene.floor.visible = true;
    for (const child of hiddenChildren) {
      child.visible = true;
    }
    hiddenChildren.length = 0;
  }

  function snapRestore(f: FocusedCup): void {
    f.body.setTranslation(f.origPos, true);
    f.body.setRotation(f.origRot, true);
    physics.setCupInert(f.group, false);
    showAll();
  }

  function enter(group: THREE.Group): void {
    if (focused && focused.group === group && !focused.exiting) return;
    if (focused) {
      snapRestore(focused);
      focused = null;
    }

    // If the cup is currently shattered, re-assemble it before focusing —
    // shards get removed, the body re-enabled. The lerp below will then
    // fly the now-intact cup from its last pre-break position to the
    // camera target.
    physics.restoreCup(group);

    const body = physics.bodyForGroup(group);
    if (!body) return;

    const tr = body.translation();
    const rt = body.rotation();
    // Fall back to current rotation if physics doesn't know an initial one
    // (defensive — every registered cup has one).
    const restingRot = physics.initRotForGroup(group) ?? rt;
    focused = {
      body,
      group,
      origPos: { x: tr.x, y: tr.y, z: tr.z },
      origRot: { x: rt.x, y: rt.y, z: rt.z, w: rt.w },
      origCameraPos: scene.camera.position.clone(),
      focusRot: {
        x: restingRot.x,
        y: restingRot.y,
        z: restingRot.z,
        w: restingRot.w,
      },
      exiting: false,
    };

    // Detach the cup's colliders and disable the body so the (still-
    // present) shelf collider can't push it around while the lerp drives
    // it toward the camera target. setEnabled(false) on its own leaves
    // colliders in Rapier compat 0.19's broadphase, which produces a
    // ~0.06 m vertical offset when the focused cup overlaps the shelf
    // board, plus a residual oscillation that prevents the exit-lerp
    // convergence check from firing snapRestore.
    physics.setCupInert(group, true);

    hideOthers(group);
  }

  function exit(): void {
    if (!focused) return;
    focused.exiting = true;
  }

  scene.onUpdate((dt) => {
    if (!focused) return;
    const f = focused;
    const t = 1 - Math.exp(-APPROACH_RATE * dt);

    let targetRot: { x: number; y: number; z: number; w: number };
    if (f.exiting) {
      targetPos.set(f.origPos.x, f.origPos.y, f.origPos.z);
      targetRot = f.origRot;
    } else {
      targetPos.copy(scene.controls.target);
      targetRot = f.focusRot;
    }

    const cur = f.body.translation();
    const next = {
      x: THREE.MathUtils.lerp(cur.x, targetPos.x, t),
      y: THREE.MathUtils.lerp(cur.y, targetPos.y, t),
      z: THREE.MathUtils.lerp(cur.z, targetPos.z, t),
    };
    // setTranslation/setRotation on a disabled body just update the stored
    // pose. No simulation, no contacts, no impulses to neighbors.
    f.body.setTranslation(next, false);

    const curRot = f.body.rotation();
    tmpQuatCur.set(curRot.x, curRot.y, curRot.z, curRot.w);
    tmpQuatTarget.set(targetRot.x, targetRot.y, targetRot.z, targetRot.w);
    tmpQuatCur.slerp(tmpQuatTarget, t);
    f.body.setRotation(
      { x: tmpQuatCur.x, y: tmpQuatCur.y, z: tmpQuatCur.z, w: tmpQuatCur.w },
      false,
    );

    if (f.exiting) {
      scene.camera.position.lerp(f.origCameraPos, t);
    } else {
      camDir.copy(scene.camera.position).sub(scene.controls.target).normalize();
      targetCamPos
        .copy(scene.controls.target)
        .addScaledVector(camDir, FOCUS_CAMERA_DISTANCE);
      scene.camera.position.lerp(targetCamPos, t);
    }

    if (f.exiting) {
      const dx = next.x - f.origPos.x;
      const dy = next.y - f.origPos.y;
      const dz = next.z - f.origPos.z;
      if (dx * dx + dy * dy + dz * dz < ARRIVED_EPSILON * ARRIVED_EPSILON) {
        snapRestore(f);
        focused = null;
      }
    }
  });

  return {
    enter,
    exit,
    isActive: () => focused !== null,
  };
}
